import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// Operations Profile — "How You Operate"
// Source: Centered Partners revised client intake (2026-06-11), Tab 3.
// The facts no policy or loss run captures: staffing, residents, planned
// changes, and how money is handled. Prospect-facing; everything else about
// the account comes from uploaded documents (policies, loss runs, rent roll,
// X-Mod worksheet, vehicle/driver lists).
// ---------------------------------------------------------------------------

const OPS = HAB_MH_CONDITION;

export const operationsProfileQuestions: Question[] = [
  // ---------------------------------------------------------------------
  // Operations & staffing
  // ---------------------------------------------------------------------
  {
    id: 'ops_staff',
    text: 'On-site W-2 staff — how many, and in what roles?',
    subtext: 'Manager, maintenance, groundskeeper, etc.',
    section: 'operations',
    group: 'ops_staffing',
    sort_order: 400,
    field_type: 'text',
    placeholder: '3 — manager, 2 maintenance',
    required: true,
    field_path: 'operations.staff',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_1099_use',
    text: 'Do you use 1099 contractors for any regular work?',
    section: 'operations',
    group: 'ops_staffing',
    sort_order: 405,
    field_type: 'boolean',
    required: true,
    field_path: 'operations.contractor_1099_use',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_1099_detail',
    text: 'What work do they perform — and do you require certificates of insurance?',
    section: 'operations',
    group: 'ops_staffing',
    sort_order: 410,
    field_type: 'textarea',
    placeholder: 'Lawn care and snow removal; COIs collected annually.',
    required: true,
    field_path: 'operations.contractor_1099_detail',
    conditions: [...OPS, { field: 'ops_1099_use', operator: 'eq', value: true }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_safety_program',
    text: 'Do you have a written safety program or return-to-work program?',
    section: 'operations',
    group: 'ops_staffing',
    sort_order: 415,
    field_type: 'select',
    options: [
      { label: 'Both', value: 'both' },
      { label: 'Safety program only', value: 'safety' },
      { label: 'Return-to-work only', value: 'rtw' },
      { label: 'Neither', value: 'none' },
    ],
    required: true,
    field_path: 'operations.safety_program',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },

  // ---------------------------------------------------------------------
  // Residents & liability
  // ---------------------------------------------------------------------
  {
    id: 'ops_screening',
    text: 'Resident screening — what do you check?',
    subtext: 'Select all that apply.',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 420,
    field_type: 'multiselect',
    options: [
      { label: 'Credit', value: 'credit' },
      { label: 'Criminal history', value: 'criminal' },
      { label: 'Eviction history', value: 'eviction_history' },
      { label: 'Income verification', value: 'income' },
      { label: 'No formal screening', value: 'none' },
    ],
    required: true,
    field_path: 'operations.screening',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_pet_policy',
    text: 'What is your pet policy?',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 425,
    field_type: 'select',
    options: [
      { label: 'No pets allowed', value: 'no_pets' },
      { label: 'Allowed with limits (breed, size, number)', value: 'restricted' },
      { label: 'Allowed without restrictions', value: 'unrestricted' },
    ],
    required: true,
    field_path: 'operations.pet_policy',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_pet_detail',
    text: 'Describe the limits',
    subtext: 'Breed restrictions, size or weight limits, number of pets.',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 430,
    field_type: 'textarea',
    placeholder: 'Max 2 pets, 50 lb limit, restricted breed list enforced at application.',
    required: true,
    field_path: 'operations.pet_detail',
    conditions: [...OPS, { field: 'ops_pet_policy', operator: 'eq', value: 'restricted' }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_bite_incidents',
    text: 'Any prior dog bite incidents?',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 435,
    field_type: 'boolean',
    required: true,
    field_path: 'operations.bite_incidents',
    conditions: [...OPS, { field: 'ops_pet_policy', operator: 'neq', value: 'no_pets' }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_bite_detail',
    text: 'Describe the incidents',
    subtext: 'Dates, severity, whether claims were filed.',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 440,
    field_type: 'textarea',
    required: true,
    field_path: 'operations.bite_detail',
    conditions: [...OPS, { field: 'ops_bite_incidents', operator: 'eq', value: true }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_evictions',
    text: 'How are evictions handled?',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 445,
    field_type: 'select',
    options: [
      { label: 'In-house', value: 'in_house' },
      { label: 'Outside attorney', value: 'attorney' },
      { label: 'Both', value: 'both' },
    ],
    required: true,
    field_path: 'operations.eviction_process',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_evictions_per_year',
    text: 'Roughly how many evictions per year?',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 450,
    field_type: 'number',
    placeholder: '3',
    required: true,
    field_path: 'operations.evictions_per_year',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_section8_pct',
    text: 'Section 8 / voucher participation — approximate % of units or pads?',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 455,
    field_type: 'percentage',
    placeholder: '0',
    required: true,
    validation: { min: 0, max: 100 },
    field_path: 'operations.section8_pct',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_amenities',
    text: 'Which amenities are on site?',
    subtext: 'Select all that apply, across all properties.',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 460,
    field_type: 'multiselect',
    options: [
      { label: 'Swimming pool', value: 'pool' },
      { label: 'Hot tub / spa', value: 'hot_tub' },
      { label: 'Playground', value: 'playground' },
      { label: 'Dog park', value: 'dog_park' },
      { label: 'Clubhouse', value: 'clubhouse' },
      { label: 'Fitness center', value: 'fitness' },
      { label: 'None of these', value: 'none' },
    ],
    required: true,
    field_path: 'operations.amenities',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_pool_fenced',
    text: 'Is the pool fenced with self-latching gates?',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 465,
    field_type: 'boolean',
    required: true,
    field_path: 'operations.pool_fenced',
    conditions: [...OPS, { field: 'ops_amenities', operator: 'contains', value: 'pool' }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_pool_depth',
    text: 'What is the pool depth?',
    subtext: 'Note any diving board or slide.',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 470,
    field_type: 'text',
    placeholder: '3–8 ft, no diving board',
    required: true,
    field_path: 'operations.pool_depth',
    conditions: [...OPS, { field: 'ops_amenities', operator: 'contains', value: 'pool' }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_security',
    text: 'On-site security measures?',
    subtext: 'Select all that apply.',
    section: 'operations',
    group: 'ops_residents',
    sort_order: 475,
    field_type: 'multiselect',
    options: [
      { label: 'Cameras', value: 'cameras' },
      { label: 'Gated entry', value: 'gated' },
      { label: 'Upgraded lighting', value: 'lighting' },
      { label: 'Guard service', value: 'guard' },
      { label: 'None of these', value: 'none' },
    ],
    required: true,
    field_path: 'operations.security',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },

  // ---------------------------------------------------------------------
  // Planned changes (next 12 months)
  // ---------------------------------------------------------------------
  {
    id: 'ops_planned_changes',
    text: 'Any new construction, addition, major renovation, or pad-site expansion planned in the coming year?',
    section: 'operations',
    group: 'ops_changes',
    sort_order: 480,
    field_type: 'boolean',
    required: true,
    field_path: 'operations.planned_changes',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_planned_changes_detail',
    text: 'Briefly describe the project, rough budget, and timing',
    subtext: 'Course-of-construction coverage is often missed and costly to add late.',
    section: 'operations',
    group: 'ops_changes',
    sort_order: 485,
    field_type: 'textarea',
    placeholder: 'Adding 30 pad sites at Oak Meadows, ~$1.5M, breaking ground in September.',
    required: true,
    field_path: 'operations.planned_changes_detail',
    conditions: [...OPS, { field: 'ops_planned_changes', operator: 'eq', value: true }],
    tags: ['operations', 'profile'],
  },

  // ---------------------------------------------------------------------
  // How money is handled
  // ---------------------------------------------------------------------
  {
    id: 'ops_pms',
    text: 'Which property management software do you use?',
    section: 'operations',
    group: 'ops_money',
    sort_order: 490,
    field_type: 'select',
    options: [
      { label: 'Yardi', value: 'yardi' },
      { label: 'RealPage', value: 'realpage' },
      { label: 'AppFolio', value: 'appfolio' },
      { label: 'Buildium', value: 'buildium' },
      { label: 'Rent Manager', value: 'rent_manager' },
      { label: 'Other', value: 'other' },
      { label: 'None', value: 'none' },
    ],
    required: true,
    field_path: 'operations.pms',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_pms_other',
    text: 'Which system?',
    section: 'operations',
    group: 'ops_money',
    sort_order: 491,
    field_type: 'text',
    required: true,
    field_path: 'operations.pms_other',
    conditions: [...OPS, { field: 'ops_pms', operator: 'eq', value: 'other' }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_rent_mix',
    text: 'How is rent collected — roughly what % online, money order, cash?',
    section: 'operations',
    group: 'ops_money',
    sort_order: 492,
    field_type: 'text',
    placeholder: '80% online, 15% money order, 5% cash',
    required: true,
    field_path: 'operations.rent_mix',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_cash_handling',
    text: 'Any on-site cash handling?',
    section: 'operations',
    group: 'ops_money',
    sort_order: 493,
    field_type: 'boolean',
    required: true,
    field_path: 'operations.cash_handling',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_cash_limits',
    text: 'Approximate daily or weekly cash limits',
    section: 'operations',
    group: 'ops_money',
    sort_order: 494,
    field_type: 'text',
    placeholder: '$200 petty cash; cash rent deposited daily',
    required: true,
    field_path: 'operations.cash_limits',
    conditions: [...OPS, { field: 'ops_cash_handling', operator: 'eq', value: true }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_wire_authority',
    text: 'Wire transfers — single approver or dual approval?',
    section: 'operations',
    group: 'ops_money',
    sort_order: 495,
    field_type: 'select',
    options: [
      { label: 'Dual approval required', value: 'dual' },
      { label: 'Single approver', value: 'single' },
      { label: 'We don’t send wires', value: 'none' },
    ],
    required: true,
    field_path: 'operations.wire_authority',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_wire_callback',
    text: 'Do you call back to a known number to verify wire requests?',
    section: 'operations',
    group: 'ops_money',
    sort_order: 496,
    field_type: 'boolean',
    required: true,
    field_path: 'operations.wire_callback',
    conditions: [...OPS, { field: 'ops_wire_authority', operator: 'neq', value: 'none' }],
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_mfa',
    text: 'Is multi-factor authentication (MFA) turned on for email and financial systems?',
    section: 'operations',
    group: 'ops_money',
    sort_order: 497,
    field_type: 'boolean',
    required: true,
    field_path: 'operations.mfa',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_cyber_incidents',
    text: 'Any cyber incident in the last 5 years?',
    subtext: 'Ransomware, business email compromise, phishing loss.',
    section: 'operations',
    group: 'ops_money',
    sort_order: 498,
    field_type: 'boolean',
    required: true,
    field_path: 'operations.cyber_incidents',
    conditions: OPS,
    tags: ['operations', 'profile'],
  },
  {
    id: 'ops_cyber_detail',
    text: 'Describe the incident(s)',
    section: 'operations',
    group: 'ops_money',
    sort_order: 499,
    field_type: 'textarea',
    required: true,
    field_path: 'operations.cyber_detail',
    conditions: [...OPS, { field: 'ops_cyber_incidents', operator: 'eq', value: true }],
    tags: ['operations', 'profile'],
  },
];
